// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.GridView.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

import GridLines from './Izenda.GridLines';
import GridItemsContainer from './Izenda.GridItemsContainer';
import { observer, propTypes as MobxPropTypes } from 'mobx-react';
import classnames from 'classnames';
import ensure from 'share/utils/Izenda.Ensure';
import { isBoolean, get, isEqual } from 'lodash/fp';
import { maxBy } from 'lodash';
import uiState from 'logic/Izenda.UiState';

@observer
export default class GridView extends Component {
  static propTypes = {
    layoutStore: MobxPropTypes.observableObject,
    layoutData: PropTypes.array,
    // Allow user to change grid view items & layout
    editable: PropTypes.bool,
    // Grid view to automatically snap items to left side.
    snapToGrid: PropTypes.bool,
    // Allow user to add a grid item inline.
    allowAddInline: function (props, propName) {
      const { editable } = props;
      const value = props[propName];
      ensure(isBoolean(value), 'allowAddInline must be boolean');
      ensure(editable || !value, 'Only allowAddInline when is editable');
    },
    // element to optionally show when user clicks + in a grid cell
    gridItemMenu: PropTypes.element,
    // On adding an grid item
    onAddItem: PropTypes.func,
    // On updating grid items
    onUpdateItemPositions: PropTypes.func,
    // Show loading while interacting
    showLoading: PropTypes.bool,
    printingMode: PropTypes.bool
  };

  static defaultProps = {
    editable: false,
    allowAddInline: false,
    showLoading: true,
    snapToGrid: false,
    printingMode: false
  };

  constructor(props: any) {
    super(props);
    const { layoutStore, layoutData, snapToGrid, printingMode } = this.props;

    !printingMode && layoutStore.setSnapToGrid(snapToGrid);
    layoutStore.setLayout(layoutData, true);

    this.onMoveStart = this.onInteractStart(true /* move */);
    this.onMove = this.onInteracting(true /* move */);
    this.onMoveStop = this.onInteractStop(true /* move */);
    this.onResizeStart = this.onInteractStart(false /* not move */);
    this.onResize = this.onInteracting(false /* not move */);
    this.onResizeStop = this.onInteractStop(false /* not move */);
  }

  componentWillReceiveProps(nextProps: any) {
    const { snapToGrid, layoutData } = this.props;
    const { layoutStore } = nextProps;
    if (nextProps.snapToGrid !== snapToGrid && !nextProps.printingMode) {
      layoutStore.setSnapToGrid(nextProps.snapToGrid);
    }
    if (!isEqual(nextProps.layoutData, layoutData)) {
      const gridItemsContainer = ReactDOM.findDOMNode(this.gridItemsContainer);
      if (
        gridItemsContainer &&
        gridItemsContainer.clientWidth > 0 &&
        gridItemsContainer.clientWidth !== layoutStore.innerWidth
      ) {
        layoutStore.setViewWidth(gridItemsContainer.clientWidth);
      }

      layoutStore.setLayout(nextProps.layoutData, false);
      layoutStore.resize = false;
      layoutStore.layout.length && nextProps.onUpdateItemPositions && nextProps.onUpdateItemPositions(layoutStore.layout.map(
        item => ({
          id: item.id,
          top: item.gridTop,
          left: item.gridLeft,
          width: item.gridWidth,
          height: item.gridHeight
        })
      ));
    }
  }

  componentDidUpdate() {
    const {
      layoutStore,
      layoutStore: { isFullSize, fullSizedItem, isSnapEditOn }
    } = this.props;
    const fullSize = isFullSize || !!fullSizedItem;
    // Scroll to top left corner of view port
    fullSize && this.scrollTo({ top: 0, left: 0 });

    isSnapEditOn && layoutStore.compactLayout();
  }

  setGridItemsContainer = (ref: any): void => {
    this.gridItemsContainer = ref;
  };

  getHeaderHeight = () => {
    const { editable, className, layoutData, layoutStore } = this.props;
    let renderHeight = layoutStore.height;
    if (uiState.isMobile && (className === 'report-format-footer' || className === 'report-format-header')) {
      renderHeight = 'auto';
    }
    if (!uiState.isMobile && className === 'report-format-header' && layoutData && !editable) {
      const elementMaxYAxis = layoutData.map(data => data.height + data.y);
      const maxYAxis = elementMaxYAxis.sort((first, second) => first - second)[elementMaxYAxis.length - 1];
      renderHeight = maxYAxis * 25;
    }
    return renderHeight;
  }

  render() {
    const {
      layoutStore,
      editable,
      allowAddInline,
      onAddItem,
      className,
      gridItemMenu,
      printingMode,
      maxWidth,
      horizontalAlign,
      children,
      fullHeight,
      stickyScrollBottom,
      alwaysShowHorizontalScroll,
      alwaysShowVerticalScroll,
      gridHeight
    } = this.props;
    const { fullSizedItem, isFullSize, height, width } = layoutStore;
    const gridViewClasses = classnames('izenda-GridView', className);
    let renderHeight = height;
    const fullSize = isFullSize || !!fullSizedItem;
    renderHeight = this.getHeaderHeight();
    const style = {
      height: !printingMode ? renderHeight : null
    };

    // Only show grid lines in editing mode.
    const gridLines = editable ? (
      <GridLines
        layoutStore={layoutStore}
        allowAddInline={allowAddInline}
        gridItemMenu={gridItemMenu}
        onAddItem={onAddItem}
        editable={editable}
        renderHeight={renderHeight}
      />
    ) : null;

    const gridItems = React.Children.map(children, this.attachViewProps);

    const scrollBoxStyle = this.getScrollBoxStyle();
    if (horizontalAlign) {
      scrollBoxStyle.width = maxWidth ? maxWidth : width;
      scrollBoxStyle.margin = 'auto';
    }
    const viewClassName = printingMode ? 'u-posRelative' : '';
    return (
      <GridItemsContainer
        stickyScrollBottom={stickyScrollBottom}
        alwaysShowHorizontalScroll={alwaysShowHorizontalScroll}
        alwaysShowVerticalScroll={alwaysShowVerticalScroll}
        ref={this.setGridItemsContainer}
        printingMode={printingMode}
        className={gridViewClasses}
        style={style}
        editable={editable}
        fullSize={fullSize}
        fullHeight={fullHeight}
        scrollBoxStyle={scrollBoxStyle}
        viewClassName={viewClassName}
        gridLines={gridLines}
        gridItems={gridItems}
        layoutStore={layoutStore}
      />
    );
  }

  /**
   * Attach grid view properties to grid item
   */
  attachViewProps = (gridItem: any): any => {
    const { layoutStore, editable, showLoading } = this.props;
    const {
      infinityRows,
      infinityCols,
      fullSizedItem,
      cellWidth,
      cellHeight,
      minItemWidth,
      minItemHeight,
      gridWidth,
      gridHeight,
      width,
      height
    } = layoutStore;

    const { id, useItemMinHeight, minHeight } = gridItem.props;
    const layoutItem = layoutStore.getLayoutItem(id);
    const isHidden = !!fullSizedItem && !layoutItem.isFullSize;

    var childProps = {
      editable,
      // Attach layout item
      layoutItem,
      // Attach scrollbox
      scrollTo: this.scrollTo,
      getScrollTop: this.getScrollTop,
      // Attach grid cell dimension
      cellWidth: cellWidth,
      cellHeight: cellHeight,
      // Min size
      minWidth: minItemWidth,
      minHeight: useItemMinHeight ? minHeight : minItemHeight,
      // Max size
      maxWidth: infinityCols ? Infinity : gridWidth,
      maxHeight: infinityRows ? Infinity : gridHeight,
      // Attach move & resize handler
      onMoveStop: this.onMoveStop,
      onMoveStart: this.onMoveStart,
      onMove: this.onMove,
      onResizeStop: this.onResizeStop,
      onResizeStart: this.onResizeStart,
      onResize: this.onResize,
      onSizeChanged: this.onItemSizeChanged,
      // Show loading while interacting
      showLoading,
      isHidden,
      containerWidth: width,
      containerHeight: height
    };

    return React.cloneElement(gridItem, childProps);
  };

  /**
   * Get style for scrollbox inside grid view
   */
  getScrollBoxStyle(): any {
    const { gridHeight, layoutStore, printingMode, forDashboard, fullHeight } = this.props;
    const { height: viewHeight } = layoutStore;
    if (fullHeight) return {};
    if (gridHeight && viewHeight > gridHeight) return { height: gridHeight };
    return printingMode && !forDashboard
      ? {
        minHeight: viewHeight + 'px',
        overflow: 'hidden'
      }
      : {
        height: viewHeight + 'px'
      };
  }

  /**
   * Get scroll box
   */
  getScrollBox(): any {
    return get('refs.scrollBox', this.gridItemsContainer);
  }

  /**
   * Get scroll top position of scroll box
   */
  getScrollTop = (): number => {
    const scrollBox = this.getScrollBox();
    return scrollBox ? scrollBox.getScrollTop() : 0;
  };

  /**
   * Scroll to particular position
   */
  scrollTo = (pos: any): void => {
    const scrollBox = this.getScrollBox();
    return scrollBox && scrollBox.scrollTo(pos);
  };

  /**
   * On start moving or resizing
   */
  onInteractStart = (move: boolean) => (layoutItem: any, pos: any) => {
    const { layoutStore } = this.props;
    layoutStore.setOverlayPosition(pos);
  };

  /**
   * On stop moving or resizing
   * @param  {Boolean} move Moving or resizing
   */
  onInteractStop = (move: boolean) => (layoutItem: any, pos: any) => {
    const {
      layoutStore,
      layoutStore: { isSnapEditOn }
    } = this.props;
    const onUpdateItemPositions = this.props.onUpdateItemPositions;

    if (isSnapEditOn) {
      const compactedLayout = layoutStore.getLastCompactedLayout();
      compactedLayout.length > 0 && onUpdateItemPositions && onUpdateItemPositions(compactedLayout, true);
    } else {
      const changedItemPositions = layoutStore.updatePosition(layoutItem, pos, move);
      onUpdateItemPositions && onUpdateItemPositions(changedItemPositions, true);
    }
  };

  /**
   * On moving or resizing
   * @param  {Boolean} move Moving or resizing
   */
  onInteracting = (move: boolean) => (layoutItem: any, pos: any) => {
    const {
      layoutStore,
      layoutStore: { isSnapEditOn }
    } = this.props;
    const snappedPosition = layoutStore.snap(pos);

    if (isSnapEditOn) {
      layoutStore.snapArrange(layoutItem.id, snappedPosition, move);
    } else {
      layoutStore.setOverlayPosition(snappedPosition);
    }
  };

  onItemSizeChanged = (item: any, width: number, height: number): void => {
    if (item.isLoading && item.isLoading()) {
      return;
    }

    const { layoutStore, onUpdateItemPositions } = this.props;
    const { gridWidth: oldItemWidth, gridHeight: oldItemHeight } = item;
    layoutStore.resize = true;
    layoutStore.updateLayoutItemSize(item, width, height);

    if (oldItemWidth !== item.gridWidth || oldItemHeight !== item.gridHeight) {
      onUpdateItemPositions &&
        onUpdateItemPositions([
          {
            id: item.id,
            top: item.gridTop,
            left: item.gridLeft,
            width: item.gridWidth,
            height: item.gridHeight
          }
        ]);
    }
  };
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/GridLayout/Izenda.GridView.js